import { useEffect, useRef, useState, useCallback } from "react";

const AUDIO_PREFIX = 0x01;
const CONTROL_PREFIX = 0x02;

/**
 * Manages a binary WebSocket connection for the CRAI POC assistant.
 *
 * @param {string|null} sessionId - Active session ID (null = don't connect)
 * @param {object} handlers - { onAudio(ArrayBuffer), onControl(object) }
 * @param {object} [options] - { wsPath: string } path prefix before session_id (no trailing slash)
 * @returns {{ status, sendAudio, sendControl, sendText, switchMode, close }}
 */
export default function useWebSocket(sessionId, handlers, { wsPath = "/v1/crai-poc/ws" } = {}) {
  const wsRef = useRef(null);
  const [status, setStatus] = useState("disconnected"); // connecting | connected | disconnected

  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const sendAudio = useCallback((pcmBuffer) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const prefixed = new Uint8Array(1 + pcmBuffer.byteLength);
    prefixed[0] = AUDIO_PREFIX;
    prefixed.set(new Uint8Array(pcmBuffer), 1);
    ws.send(prefixed.buffer);
  }, []);

  const sendControl = useCallback((obj) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const json = new TextEncoder().encode(JSON.stringify(obj));
    const prefixed = new Uint8Array(1 + json.byteLength);
    prefixed[0] = CONTROL_PREFIX;
    prefixed.set(json, 1);
    ws.send(prefixed.buffer);
  }, []);

  const sendText = useCallback((text) => {
    sendControl({ type: "text_input", text });
  }, [sendControl]);

  const switchMode = useCallback((mode) => {
    sendControl({ type: "mode_change", mode });
  }, [sendControl]);

  const close = useCallback(() => {
    const ws = wsRef.current;
    if (ws) {
      try {
        ws.onclose = null;
        ws.onerror = null;
        ws.close();
      } catch {
        // ignore close errors
      }
      wsRef.current = null;
    }
    // Ensure local status reflects that we are no longer connected
    setStatus("disconnected");
  }, []);

  useEffect(() => {
    if (!sessionId) return;

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    // const url = `${protocol}//${"demo.orie.ai/api/ai"}${wsPath}/${sessionId}`;
    const url = `${"wss://demo.orie.ai/api/ai"}${wsPath}/${sessionId}`;

    setStatus("connecting");
    const ws = new WebSocket(url);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;

    ws.onopen = () => setStatus("connected");

    ws.onmessage = (event) => {
      const data = event.data;
      if (!(data instanceof ArrayBuffer) || data.byteLength < 2) return;

      const view = new Uint8Array(data);
      const prefix = view[0];
      const payload = data.slice(1);

      if (prefix === AUDIO_PREFIX) {
        handlersRef.current.onAudio?.(payload);
      } else if (prefix === CONTROL_PREFIX) {
        let msg;
        try {
          msg = JSON.parse(new TextDecoder().decode(payload));
        } catch {
          // ignore malformed JSON
          return;
        }
        try {
          handlersRef.current.onControl?.(msg);
        } catch (err) {
          console.error("[useWebSocket] error in onControl handler:", err, msg);
        }
      }
    };

    ws.onclose = () => setStatus("disconnected");
    ws.onerror = () => setStatus("disconnected");

    return () => {
      try {
        ws.close();
      } catch {
        // ignore close errors
      }
      if (wsRef.current === ws) {
        wsRef.current = null;
      }
      setStatus("disconnected");
    };
  }, [sessionId]);

  return { status, sendAudio, sendControl, sendText, switchMode, close };
}
